import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Grid,
  Dialog,
  IconButton,
  Typography,
  makeStyles,
  withStyles,
  Switch,
  FormGroup,
} from "@material-ui/core";
import Select from "@jll/select";
import TextField from "../../../../../../Component/Forms/TextField";
import CloseIcon from "@material-ui/icons/Close";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import InputLabel from "@material-ui/core/InputLabel";
import ModalDialogContext from "../../../../../../Context/ModalDialog/ModalDialogContext";
import LoadingIndicator from "../../../../../../Component/LoadingIndicator";
import SearchableSelect from "../../../../../../Component/Forms/SearchableSelect";
import { processPermissionState } from "../ViewRule/processPermissionState";
import { addRecordAccessRule } from "../../../../../../API/AccessControl";
import { getPermissionsdataforDbFromScratch } from "./processPermissionForDb";

import styles from "./styles";
import { useTranslation } from "react-i18next";
import { recordTypes } from "../../../../../../API/config";
import { defaultPermissionsofRules } from "../../defaultPermissionsofRules";
import DisplayPermissions from "./displayPermissionsForAddRule";
import DisplayUserLocations from "./displayUserLocations";
import CustomButton from "../../../../../../Component/CustomButton";

const useStyles = makeStyles(styles);

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const renderLoadingIndicator = () => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        // height: "70vh",
      }}
    >
      <LoadingIndicator />
    </div>
  );
};

const changePermissionForGlobal = (permissions, checkedStatus) => {
  const newPermissions = JSON.parse(JSON.stringify(permissions));
  newPermissions["*"] = { "*": checkedStatus };
  for (let obj in newPermissions) {
    if (newPermissions[obj].hasOwnProperty("*")) {
      newPermissions[obj]["*"] = checkedStatus;
    }
    for (let action in newPermissions[obj]) {
      if (newPermissions[obj][action].hasOwnProperty("accessStatus"))
        newPermissions[obj][action]["accessStatus"] = checkedStatus;
    }
  }
  return newPermissions;
};

const changePermissionForRecordGlobal = (
  permissions,
  record,
  checkedStatus
) => {
  if (!permissions || !record) return;
  const newPermissions = JSON.parse(JSON.stringify(permissions));
  newPermissions[record.name]["*"] = checkedStatus;
  for (let obj in newPermissions[record.name]) {
    if (newPermissions[record.name][obj].hasOwnProperty("accessStatus"))
      newPermissions[record.name][obj]["accessStatus"] = checkedStatus;
  }
  return newPermissions;
};

const AddRule = (props) => {
  const {
    open,
    onclose,
    rules,
    entityCategories,
    entitySubCategories,
    geographiesData,
    countriesDataForAccessRole,
    processSequencesForAccessRole,
    reloadUiAfterSubmit,
    allUserGroups = [],
  } = props;
  const classes = useStyles();

  const [isDialogOpen, setIsDialogOpen] = useState(open);
  const [name, setName] = useState();
  const [description, setDescription] = useState();
  const [locations, setLocations] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [selectedRule, setSelectedRule] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [userType, setUserType] = useState("internal");
  const [allRules, setAllRules] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [allRulesToDisplay, setAllRulesToDisplay] = useState([]);
  const [selectedUserGroups, setSelectedUserGroups] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const { showErrorMessage, showLoadingMessage, showMessage } =
    useContext(ModalDialogContext);

  useEffect(() => {
    if (open) {
      getAllData();
    }
  }, [open]);

  const handleClose = () => {
    setIsDialogOpen(false);
    setName();
    setDescription();
    setLocations();
    setUserType();
    setPermissions();
    if (onclose) {
      onclose("cancel");
    }
  };

  const getAllData = async () => {
    setIsDialogOpen(true);
    setAllRules(rules);
    setSelectedUserGroups([]);
    const defaultPermissions = defaultPermissionsofRules(
      entityCategories,
      entitySubCategories,
      geographiesData,
      countriesDataForAccessRole,
      processSequencesForAccessRole
    );
    setAllRulesToDisplay(
      rules.map((rule) => ({
        label: rule.name,
        value: rule.id,
      }))
    );
    setPermissions(defaultPermissions);
  };

  const changeInDefaultPermissions = useCallback(
    (recordTypeName, actionName, updatePermission) => {
      setPermissions((prevVal) => ({
        ...prevVal,
        [recordTypeName]: {
          ...prevVal[recordTypeName],
          [actionName]: {
            ...prevVal[recordTypeName][actionName],
            conditions: updatePermission,
          },
        },
      }));
    },
    [setPermissions]
  );

  const handleChange = useCallback(
    (event, fieldName, actionName, recordType) => {
      if (fieldName === "permissions") {
        event.persist();
        const checkedStatus = event.target.checked;
        setPermissions((prevVal) => ({
          ...prevVal,
          [recordType]: {
            ...prevVal[recordType],
            [actionName]: {
              ...prevVal[recordType][actionName],
              accessStatus: checkedStatus,
            },
          },
        }));
      }
    },
    [setPermissions]
  );

  const handleChangeInGlobalPermission = (event) => {
    if (event) {
      const checkedStatus = event.target.checked;
      const newPermissions = changePermissionForGlobal(
        permissions,
        checkedStatus
      );
      setPermissions(newPermissions);
    }
  };

  const isValidForm = () => {
    const errors = {};
    let hasError = false;
    if (!name || typeof name !== "string" || (name || "").trim().length < 1) {
      errors["name"] = "should not be empty";
      hasError = true;
    }
    if (
      !description ||
      typeof description !== "string" ||
      (description || "").trim().length < 1
    ) {
      errors["description"] = "should not be empty";
      hasError = true;
    }

    if (hasError) {
      setFormErrors(errors);
    }
    return hasError;
  };

  const handleSubmit = async () => {
    try {
      setFormErrors({});
      const invalidEditedForm = isValidForm();
      if (invalidEditedForm) return;
      showLoadingMessage();
      setIsCreating(true);
      let processPermissionsforDb =
        getPermissionsdataforDbFromScratch(permissions);

      const updatedAccessRule = {
        name,
        userGroups: JSON.stringify(selectedUserGroups.map((ug) => ug.value)),
        userLocations: JSON.stringify(locations),
        userType,
        description,
        permissions: JSON.stringify(processPermissionsforDb),
      };
      await addRecordAccessRule(updatedAccessRule);
      showMessage({
        message: t("messages.accessControl.ruleCreated"),
      });
    } catch (error) {
      showErrorMessage({ error });
    }
    setIsCreating(false);
    handleClose();
    reloadUiAfterSubmit();
  };

  const changeRuleSelected = (event) => {
    const selectedAlreadyExistingRule = allRules.find(
      (existingRule) => existingRule.id === event.value
    );
    if (!selectedAlreadyExistingRule) return;
    const permissionsSkeleton = defaultPermissionsofRules(
      entityCategories,
      entitySubCategories,
      geographiesData,
      countriesDataForAccessRole
    );
    let { defaultPermissions } = processPermissionState(
      selectedAlreadyExistingRule["permissions"],
      permissionsSkeleton
    );
    setSelectedUserGroups(
      allUserGroups.filter(
        (sg) =>
          Array.isArray(selectedAlreadyExistingRule["userGroups"]) &&
          selectedAlreadyExistingRule["userGroups"].indexOf(sg.label) >= 0
      )
    );
    setLocations(selectedAlreadyExistingRule["userLocations"]);
    setUserType(selectedAlreadyExistingRule["userType"]);
    setSelectedRule(event);
    setPermissions(JSON.parse(JSON.stringify(defaultPermissions)));
  };

  const handleChangeinName = (value) => setName(value);

  const handleChangeinDesc = (value) => setDescription(value);

  const handleChangeInRecordGlobal = useCallback(
    (checkedStatus, record) => {
      const newPermissions = changePermissionForRecordGlobal(
        permissions,
        record,
        checkedStatus
      );
      setPermissions(newPermissions);
    },
    [setPermissions, permissions]
  );

  const handleChangeInSecuGroups = (e) => {
    if (e) {
      setSelectedUserGroups(e);
    } else {
      setSelectedUserGroups([]);
    }
  };

  const renderAddForm = () => {
    return (
      <Grid container direction="row" spacing={3}>
        <Grid item sm={6} md={6} xs={12}>
          <TextField
            label={t("labels.accessControl.rule.form.name")}
            initialValue={name}
            type="text"
            onChange={(value) => handleChangeinName(value)}
            required={true}
            helperText={formErrors["name"]}
          />
          {formErrors["name"] && (
            <div className={classes.errorText}>{formErrors["name"]}</div>
          )}
        </Grid>
        <Grid item sm={6} md={6} xs={12}>
          <TextField
            label={t("labels.accessControl.rule.form.description")}
            type="text"
            initialValue={description}
            required={true}
            onChange={(value) => handleChangeinDesc(value)}
          />
          {formErrors["description"] && (
            <div className={classes.errorText}>{formErrors["description"]}</div>
          )}
        </Grid>
        <Grid item xs={12}>
          <InputLabel className={classes.inputLabel}>
            {t("labels.accessControl.rule.form.userGroups")}
          </InputLabel>
          <FormGroup>
            <Select
              value={selectedUserGroups}
              options={allUserGroups}
              isMulti={true}
              placeholder={t("labels.common.select")}
              isSearchable={true}
              className={classes.multiSelector}
              onChange={handleChangeInSecuGroups}
              noOptionsText={t("messages.common.noMoreOptions")}
            />
          </FormGroup>
        </Grid>
        <Grid item xs={12}>
          <InputLabel className={classes.inputLabel}>
            {t("labels.accessControl.rule.form.locations")}
          </InputLabel>
          <DisplayUserLocations locations={locations} />
        </Grid>
        <Grid item xs={12}>
          <SearchableSelect
            label={t("labels.accessControl.rule.form.existingRules")}
            options={allRulesToDisplay}
            value={selectedRule}
            changeHandler={changeRuleSelected}
            disableClearable={true}
          />
        </Grid>
        <Grid item xs={12}>
          <div className={classes.inputLabel}>
            {t("labels.accessControl.rule.form.permissions")}
            <span className={classes.marginLeft}>
              <span className={classes.red}>
                {t("labels.accessControl.rule.deny")} all
              </span>
              <Switch
                checked={
                  permissions &&
                  permissions.hasOwnProperty("*") &&
                  permissions["*"].hasOwnProperty("*")
                    ? permissions["*"]["*"]
                    : false
                }
                disableRipple
                classes={{
                  switchBase: classes.switchBase,
                  thumb: classes.thumb,
                  track: classes.track,
                  checked: classes.checked,
                }}
                color="primary"
                onChange={handleChangeInGlobalPermission}
              />
              <span className={classes.green}>
                {t("labels.accessControl.rule.allow")} all
              </span>
            </span>
          </div>
          {permissions &&
            Object.keys(permissions).map((rt) => {
              if (rt === "*") return;
              return (
                <DisplayPermissions
                  key={rt}
                  handleChange={handleChange}
                  permissions={permissions}
                  recordType={recordTypes[rt]}
                  changeInDefaultPermissions={changeInDefaultPermissions}
                  handleChangeInRecordGlobal={handleChangeInRecordGlobal}
                />
              );
            })}
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Dialog
        disableBackdropClick
        onClose={handleClose}
        aria-labelledby="add-document-modal"
        open={isDialogOpen}
        fullWidth={true}
        maxWidth="md"
      >
        <DialogTitle id="add-document-modal" onClose={handleClose}>
          <Typography className={classes.panelTitle}>
            {t("labels.accessControl.rules.addRule")}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {isLoading ? renderLoadingIndicator() : renderAddForm()}
        </DialogContent>

        <DialogActions>
          <CustomButton variant="secondary" onClick={handleClose}>
            {t("labels.common.close")}
          </CustomButton>
          <CustomButton variant="primary"
           onClick={handleSubmit}
           loadingText={
              isCreating ? t("labels.check.createForm.creating") : ""
            }
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            {t("labels.common.submit")}
          </CustomButton>

        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddRule;
